import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Button from '../../../../../components/Atoms/Button.jsx';

/**
 * Zoomable / pannable image preview.
 *
 * The preview area is the panel, not the image: a small image is scaled UP to
 * fill it instead of sitting in the middle at its native pixel size, and a huge
 * one is scaled down. Wheel zooms about the cursor, drag pans, double-click
 * toggles fit <-> 100%.
 */

const MIN_ZOOM = 0.05;
const MAX_ZOOM = 40;
const ZOOM_STEP = 1.25;
const PADDING = 16;
/** Past this zoom the browser's smoothing turns a small image to mush. */
const CRISP_ABOVE = 2;

const clampZoom = (zoom) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));

const clampAxis = (translate, viewport, content) => {
  if (!content) return translate;
  if (content <= viewport) return (viewport - content) / 2;
  const slack = Math.min(96, viewport * 0.25);
  return Math.min(slack, Math.max(viewport - content - slack, translate));
};

const ImagePreviewer = ({ src, name }) => {
  const containerRef = useRef(null);
  const dragRef = useRef(null);
  const viewRef = useRef(null);
  const [natural, setNatural] = useState(null);
  const [view, setView] = useState({ zoom: 1, x: 0, y: 0, fitted: true });
  const [isDragging, setIsDragging] = useState(false);
  const [failed, setFailed] = useState(false);

  // the wheel listener is registered once; it reads the live view from here
  viewRef.current = view;

  const measure = useCallback(() => {
    const box = containerRef.current?.getBoundingClientRect();
    return { width: box?.width || 0, height: box?.height || 0 };
  }, []);

  const fit = useCallback((size = natural) => {
    if (!size) return;
    const { width, height } = measure();
    if (!width || !height) return;
    const zoom = clampZoom(
      Math.min((width - PADDING * 2) / size.width, (height - PADDING * 2) / size.height)
    );
    setView({
      zoom,
      x: (width - size.width * zoom) / 2,
      y: (height - size.height * zoom) / 2,
      fitted: true,
    });
  }, [measure, natural]);

  const zoomAt = useCallback((nextZoom, anchor) => {
    if (!natural) return;
    setView((current) => {
      const zoom = clampZoom(nextZoom);
      const { width, height } = measure();
      const point = anchor || { x: width / 2, y: height / 2 };
      const ratio = zoom / current.zoom;
      return {
        zoom,
        x: clampAxis(point.x - (point.x - current.x) * ratio, width, natural.width * zoom),
        y: clampAxis(point.y - (point.y - current.y) * ratio, height, natural.height * zoom),
        fitted: false,
      };
    });
  }, [measure, natural]);

  // new image -> measure it, then fit
  useEffect(() => {
    setNatural(null);
    setFailed(false);
    if (!src) return undefined;
    const probe = new Image();
    let cancelled = false;
    probe.onload = () => {
      if (cancelled) return;
      setNatural({ width: probe.naturalWidth, height: probe.naturalHeight });
    };
    probe.onerror = () => {
      if (!cancelled) setFailed(true);
    };
    probe.src = src;
    return () => {
      cancelled = true;
    };
  }, [src]);

  useLayoutEffect(() => {
    if (natural) fit(natural);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [natural]);

  // keep the image framed when the panel resizes
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const observer = new ResizeObserver(() => {
      setView((current) => {
        if (!natural) return current;
        if (current.fitted) {
          const { width, height } = measure();
          if (!width || !height) return current;
          const zoom = clampZoom(
            Math.min((width - PADDING * 2) / natural.width, (height - PADDING * 2) / natural.height)
          );
          return {
            zoom,
            x: (width - natural.width * zoom) / 2,
            y: (height - natural.height * zoom) / 2,
            fitted: true,
          };
        }
        const { width, height } = measure();
        return {
          ...current,
          x: clampAxis(current.x, width, natural.width * current.zoom),
          y: clampAxis(current.y, height, natural.height * current.zoom),
        };
      });
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [measure, natural]);

  // wheel zoom (non-passive, so it can win over page scroll)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const onWheel = (event) => {
      event.preventDefault();
      const box = container.getBoundingClientRect();
      zoomAt(viewRef.current.zoom * Math.exp(-event.deltaY * 0.0022), {
        x: event.clientX - box.left,
        y: event.clientY - box.top,
      });
    };
    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  const onPointerDown = (event) => {
    if (event.button !== 0 || !natural) return;
    dragRef.current = { x: event.clientX, y: event.clientY };
    setIsDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event) => {
    if (!dragRef.current || !natural) return;
    const dx = event.clientX - dragRef.current.x;
    const dy = event.clientY - dragRef.current.y;
    dragRef.current = { x: event.clientX, y: event.clientY };
    setView((current) => {
      const { width, height } = measure();
      return {
        ...current,
        x: clampAxis(current.x + dx, width, natural.width * current.zoom),
        y: clampAxis(current.y + dy, height, natural.height * current.zoom),
        fitted: false,
      };
    });
  };

  const endDrag = (event) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    setIsDragging(false);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const zoomPercent = Math.round(view.zoom * 100);

  return (
    <div className="lf-preview">
      <div
        ref={containerRef}
        className="lf-preview__viewport"
        data-dragging={isDragging ? 'true' : undefined}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onDoubleClick={() => (view.fitted ? zoomAt(1) : fit())}
      >
        {natural && !failed && (
          <img
            src={src}
            alt={name || 'preview'}
            className="lf-preview__img"
            draggable={false}
            style={{
              width: natural.width,
              height: natural.height,
              transform: `translate(${view.x}px, ${view.y}px) scale(${view.zoom})`,
              imageRendering: view.zoom > CRISP_ABOVE ? 'pixelated' : 'auto',
            }}
          />
        )}
        {failed && (
          <div className="lf-preview__error">
            <i className="fa fa-exclamation-triangle" aria-hidden="true"></i>
            <p>This image could not be loaded.</p>
          </div>
        )}
      </div>

      {natural && !failed && (
        <>
          <div className="lf-preview__chip" title={name}>
            <i className="fa fa-image" aria-hidden="true"></i>
            <span className="lf-preview__name">{name || 'Untitled'}</span>
            <span className="lf-preview__dims">
              {natural.width} × {natural.height}
            </span>
          </div>

          <div className="lf-preview__controls">
            <Button
              className="lf-tool-btn lf-tool-btn--icon"
              onClick={() => zoomAt(view.zoom / ZOOM_STEP)}
              tooltipText="Zoom out"
            >
              <i className="fa fa-search-minus" aria-hidden="true"></i>
            </Button>
            <span className="lf-zoom-readout">{zoomPercent}%</span>
            <Button
              className="lf-tool-btn lf-tool-btn--icon"
              onClick={() => zoomAt(view.zoom * ZOOM_STEP)}
              tooltipText="Zoom in"
            >
              <i className="fa fa-search-plus" aria-hidden="true"></i>
            </Button>
            <Button
              className={`lf-tool-btn lf-tool-btn--icon ${view.fitted ? 'lf-tool-btn--active' : ''}`}
              onClick={() => fit()}
              tooltipText="Fit to panel"
            >
              <i className="fa fa-expand-arrows-alt" aria-hidden="true"></i>
            </Button>
            <Button
              className="lf-tool-btn lf-tool-btn--ratio"
              onClick={() => zoomAt(1)}
              tooltipText="Actual size, 100%"
            >
              1:1
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default ImagePreviewer;
